/**
 * How a client finds its well-known things.
 *
 * A webapp is built once and deployed to every environment, and the build
 * carries no environment facts — it never will, because build-time
 * configuration breaks the artifact rule (one immutable zip promoted
 * everywhere). So the app asks at boot, the same way everywhere, and stops at
 * the first answer:
 *
 *   1. An `overrides` object handed to this call. Tests and local tooling only.
 *      There is no storage, no query string, no hidden screen.
 *   2. `GET /manifest.json` from the app's own origin. The deployment wrote it
 *      beside index.html (WebappConstruct, from the environment's facts and the
 *      app's own pool client). Deployed, this is the truth.
 *   3. The well-known local ports below — only when the page is on `localhost`
 *      and there is no manifest. Sign-in needs the manifest even locally: it
 *      names the identity server and which app this is, and an app's id is a
 *      fact of the app, not of this table.
 *
 * Deployed with no manifest is a loud failure, not a guess. Deriving a service
 * host from the app's own hostname is retired: it was a crutch for the file
 * nobody wrote.
 *
 * Pure: inputs in, the manifest shape out, so it is testable without a browser.
 * The shape mirrors `configureBackends`, so the two meet without mapping:
 *
 *   {
 *     environment,
 *     vibeServer: { baseUrl },
 *     administrationServer: { baseUrl },
 *     eventRuntime: { biddingUrl, deltaWebSocketUrl },
 *     identityServer: { baseUrl, appId }
 *   }
 */

export const MANIFEST_PATH = '/manifest.json';

/**
 * The one table of well-known things and their local ports — what step 3
 * answers. `server` is NOT here because it is not a local fact; it is the
 * vibe-server subdomain, and the manifest is where that name lives.
 */
export const LOCAL_SERVICES = Object.freeze({
    environment: 'local',
    vibeServer: Object.freeze({ baseUrl: 'http://localhost:4500/api' }),
    administrationServer: Object.freeze({ baseUrl: 'http://localhost:4100' }),
    eventRuntime: Object.freeze({
        biddingUrl: 'http://localhost:4030',
        deltaWebSocketUrl: 'ws://localhost:9001'
    })
});

/**
 * @param {Object} [options]
 * @param {Object} [options.overrides] Used as the answer, untouched. Tests and local tooling.
 * @param {Function} [options.fetch]   The fetch to read the manifest with; the global one by default.
 * @param {Object} [options.location]  Something with `hostname` and `origin`; `globalThis.location` by default.
 * @returns {Promise<Object>} The manifest shape above.
 */
export async function discoverServices({ overrides, fetch = globalThis.fetch, location = globalThis.location } = {}) {
    if (overrides) {
        return overrides;
    }
    const manifest = await readManifest(fetch);
    if (manifest) {
        return manifest;
    }
    if (location?.hostname === 'localhost') {
        return structuredClone(LOCAL_SERVICES);
    }
    throw new Error(
        `No ${MANIFEST_PATH} at ${location?.origin ?? 'this origin'}. The deployment writes it beside index.html; `
        + 'locally, copy the app\'s manifest.example.json to public/manifest.json (see the app\'s README).'
    );
}

// A dev server answers an unknown path with index.html at 200 — the SPA
// fallback — so a 200 is not yet a manifest; the content type decides. A fetch
// that throws (no server at all) reads the same as a missing file.
async function readManifest(fetch) {
    if (typeof fetch !== 'function') {
        return null;
    }
    let response;
    try {
        response = await fetch(MANIFEST_PATH, { cache: 'no-cache' });
    } catch {
        return null;
    }
    if (!response?.ok) {
        return null;
    }
    const contentType = response.headers?.get?.('content-type') ?? '';
    if (!contentType.includes('application/json')) {
        return null;
    }
    try {
        const body = await response.json();
        if (body && typeof body === 'object' && !Array.isArray(body)) {
            return body;
        }
        return null;
    } catch {
        return null;
    }
}
